/* ==========================================================
   Product Showcase — behavior
   - Size options match a real product variant (via the option
     value you enter in "Matching variant option value").
   - Bundle options set the cart quantity and (optionally) a
     display-only discounted price. NOTE: this section changes
     what is *shown* and how many units are added to the cart;
     it does not itself apply a real Shopify discount. Pair a
     bundle with a matching automatic discount (Shopify Admin >
     Discounts) for the price charged at checkout to match the
     price shown here, or add a discount code below.
   - Picking a size never swaps the product photo shown in the
     media column — it stays fixed no matter which size/bundle is
     selected.
   - "Buy now" adds the current variant/quantity to the cart, same
     as Add to Cart, then redirects straight to /checkout instead
     of staying on the page (Shopify's usual dynamic-checkout
     behavior).

   FIX (page lag, for good this time): all of the buy-box logic below
   — size selection, bundle selection, add to cart, buy now — runs
   immediately when this script loads. The stagger-in animation no
   longer depends on GSAP or any other third-party library loaded
   from a CDN; it's now plain CSS transitions (see
   product-showcase.css) turned on by classList.add() calls below.
   There is no network request for this feature anymore, so it
   cannot be slow, cannot block anything else on the page, and cannot
   leave content hidden — every [data-stagger-item] is fully visible
   by default in CSS regardless of whether any of this runs at all.
   ========================================================== */

(function () {
  "use strict";

  /* Per-item delay step (seconds) within a group, and the extra delay
     added per group so the whole section cascades in turn. */
  var CONTENT_STAGGER = 0.12;
  var GROUP_DELAY = 0.12;

  function init(root) {
    var moneyFormat = root.dataset.moneyFormat || "${{amount}}";
    var redirectToCart = root.dataset.redirectToCart === "true";

    var variantsScript = root.querySelector("[data-product-variants-json]");
    var variants = [];
    if (variantsScript) {
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        variants = [];
      }
    }

    var sizeButtons = Array.prototype.slice.call(root.querySelectorAll("[data-size-option]"));
    var bundleLabels = Array.prototype.slice.call(root.querySelectorAll("[data-bundle-option]"));
    var addToCartBtn = root.querySelector("[data-add-to-cart]");
    var ctaLabelEl = addToCartBtn ? addToCartBtn.querySelector("[data-cta-label]") : null;
    var ctaPriceEl = addToCartBtn ? addToCartBtn.querySelector("[data-cta-price]") : null;

    var buyNowBtn = root.querySelector("[data-buy-now]");
    var buyNowLabelEl = buyNowBtn ? buyNowBtn.querySelector("[data-buy-now-label]") : null;

    var stickyBarEl = root.querySelector("[data-sticky-bar]");
    var stickyTitleEl = root.querySelector("[data-sticky-bar-title]");
    var stickyAddToCartBtn = root.querySelector("[data-sticky-add-to-cart]");
    var stickyCtaLabelEl = stickyAddToCartBtn ? stickyAddToCartBtn.querySelector("[data-sticky-cta-label]") : null;
    var stickyCtaPriceEl = stickyAddToCartBtn ? stickyAddToCartBtn.querySelector("[data-sticky-cta-price]") : null;
    var productTitle = root.dataset.productTitle || "";

    var currentVariant = variants[0] || null;

    /*
       FIX (buy box price out of sync with the visibly-selected size):
       currentVariant used to always start as variants[0] regardless of
       which size button was actually shown as selected on screen. Now
       it starts from whichever size button has .is-selected (falling
       back to the first size button, then to variants[0] if there are
       no size blocks at all), so the price shown on first render
       always matches the size that looks selected.
    */
    if (sizeButtons.length) {
      var initialIndex = sizeButtons.findIndex(function (b) {
        return b.classList.contains("is-selected");
      });
      if (initialIndex === -1) initialIndex = 0;
      var initialBtn = sizeButtons[initialIndex];
      if (initialBtn) {
        var initialParsedIndex = parseInt(initialBtn.dataset.index, 10);
        if (isNaN(initialParsedIndex)) initialParsedIndex = initialIndex;
        currentVariant = findVariantForLabel(initialBtn.dataset.match, initialBtn.dataset.label, initialParsedIndex) || currentVariant;
      }
    }

    /* ---------- helpers ---------- */

    function formatMoney(cents) {
      if (typeof cents !== "number" || isNaN(cents)) return "";
      var amount = (cents / 100).toFixed(2);
      return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
    }

    function normalize(str) {
      return (str || "").toString().trim().toLowerCase();
    }

    /*
       FIX (every size button showing the same price): this used to
       fall back to variants[0] whenever a size's matching text didn't
       line up exactly with a variant's option value, so two size
       buttons that both failed to match silently collapsed onto the
       same first variant and showed the same price. Now:
       1) matching is more forgiving (either string containing the
          other, not just an exact match) so small wording differences
          like "30 Days" vs "30 Days Pack" still match, and
       2) if nothing matches at all, it falls back to that button's
          own position in the variant list (fallbackIndex) instead of
          always variant #1, so distinct size buttons still show
          distinct prices even with no text match.
    */
    function findVariantForLabel(matchValue, labelFallback, fallbackIndex) {
      if (!variants.length) return null;
      var needle = normalize(matchValue || labelFallback);

      if (needle) {
        var byOption = variants.find(function (v) {
          return [v.option1, v.option2, v.option3].some(function (opt) {
            var o = normalize(opt);
            return o && (o === needle || o.indexOf(needle) !== -1 || needle.indexOf(o) !== -1);
          });
        });
        if (byOption) return byOption;

        var byTitle = variants.find(function (v) {
          var t = normalize(v.title);
          return t && (t.indexOf(needle) !== -1 || needle.indexOf(t) !== -1);
        });
        if (byTitle) return byTitle;
      }

      if (typeof fallbackIndex === "number" && variants[fallbackIndex]) {
        return variants[fallbackIndex];
      }
      return variants[0];
    }

    /*
       Sticky bar title mirrors the reference screenshot's format
       ("strawberry pineapple  6 pack" = product title + currently
       selected size label), with NO edit link/button, per instruction.
    */
    function updateStickyTitle() {
      if (!stickyTitleEl) return;
      var selectedBtn = sizeButtons.find(function (b) {
        return b.classList.contains("is-selected");
      });
      var sizeLabel = selectedBtn ? (selectedBtn.dataset.label || selectedBtn.textContent || "").trim() : "";
      stickyTitleEl.textContent = sizeLabel ? (productTitle + "  " + sizeLabel) : productTitle;
    }

    function getSelectedBundle() {
      var selected = bundleLabels.find(function (label) {
        var input = label.querySelector('input[type="radio"]');
        return input && input.checked;
      });
      return selected || bundleLabels[0] || null;
    }

    function paintSizePrices() {
      sizeButtons.forEach(function (btn, i) {
        var priceEl = btn.querySelector("[data-size-price]");
        if (!priceEl) return;
        var index = parseInt(btn.dataset.index, 10);
        if (isNaN(index)) index = i;
        var variant = findVariantForLabel(btn.dataset.match, btn.dataset.label, index);
        if (variant && typeof variant.price === "number") {
          priceEl.textContent = formatMoney(variant.price);
        }
      });
    }

    function recalc() {
      var selectedBundle = getSelectedBundle();
      var quantity = 1;
      var discount = 0;
      var priceOverride = "";
      var unitOverride = "";

      if (selectedBundle) {
        quantity = parseInt(selectedBundle.dataset.quantity, 10) || 1;
        discount = parseFloat(selectedBundle.dataset.discount) || 0;
        priceOverride = selectedBundle.dataset.priceOverride || "";
        unitOverride = selectedBundle.dataset.unitPriceOverride || "";
      }

      var totalDisplay = priceOverride;
      var unitDisplay = unitOverride;

      if (!totalDisplay && currentVariant && typeof currentVariant.price === "number") {
        var totalCents = currentVariant.price * quantity * (1 - discount / 100);
        totalDisplay = formatMoney(Math.round(totalCents));
        if (!unitDisplay) {
          unitDisplay = formatMoney(Math.round(totalCents / quantity)) + " / unit";
        }
      }

      if (selectedBundle) {
        var priceEl = selectedBundle.querySelector("[data-bundle-price]");
        var unitEl = selectedBundle.querySelector("[data-bundle-unit]");
        if (priceEl) priceEl.textContent = totalDisplay;
        if (unitEl) unitEl.textContent = unitDisplay;
      }

      if (ctaPriceEl) {
        ctaPriceEl.textContent = totalDisplay ? "— " + totalDisplay : "";
      }

      /* Sticky mobile bar price stays in sync with whichever
         size/bundle is currently selected, same as the main CTA. */
      if (stickyCtaPriceEl) {
        stickyCtaPriceEl.textContent = totalDisplay ? "— " + totalDisplay : "";
      }

      root.dataset.currentQuantity = quantity;
    }

    /* ---------- size options ---------- */

    sizeButtons.forEach(function (btn, i) {
      btn.addEventListener("click", function () {
        sizeButtons.forEach(function (b) {
          b.classList.remove("is-selected");
          b.setAttribute("aria-checked", "false");
        });
        btn.classList.add("is-selected");
        btn.setAttribute("aria-checked", "true");
        var index = parseInt(btn.dataset.index, 10);
        if (isNaN(index)) index = i;
        currentVariant = findVariantForLabel(btn.dataset.match, btn.dataset.label, index) || currentVariant;
        updateStickyTitle();
        recalc();
      });
    });

    /* ---------- bundle options ---------- */

    bundleLabels.forEach(function (label) {
      var input = label.querySelector('input[type="radio"]');
      if (!input) return;
      input.addEventListener("change", function () {
        bundleLabels.forEach(function (l) {
          l.classList.remove("is-selected");
        });
        label.classList.add("is-selected");
        recalc();
      });
    });

    /* ---------- add to cart ---------- */

    if (addToCartBtn) {
      addToCartBtn.addEventListener("click", function () {
        if (!currentVariant || addToCartBtn.disabled) return;

        var quantity = parseInt(root.dataset.currentQuantity, 10) || 1;
        var originalLabel = ctaLabelEl ? ctaLabelEl.textContent : "";
        var addingText = addToCartBtn.dataset.addingText || "adding…";
        var addedText = addToCartBtn.dataset.addedText || "added ✓";

        addToCartBtn.classList.add("is-loading");
        addToCartBtn.disabled = true;
        if (ctaLabelEl) ctaLabelEl.textContent = addingText;

        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            items: [{ id: currentVariant.id, quantity: quantity }],
          }),
        })
          .then(function (res) {
            if (!res.ok) throw new Error("Add to cart failed");
            return res.json();
          })
          .then(function () {
            if (ctaLabelEl) ctaLabelEl.textContent = addedText;
            document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));
            if (window.OBVSLYCartDrawer && typeof window.OBVSLYCartDrawer.flyToCart === "function") {
              window.OBVSLYCartDrawer.flyToCart(addToCartBtn);
            }

            if (redirectToCart) {
              window.location.href = "/cart";
              return;
            }
            setTimeout(function () {
              addToCartBtn.classList.remove("is-loading");
              addToCartBtn.disabled = false;
              if (ctaLabelEl) ctaLabelEl.textContent = originalLabel;
            }, 1800);
          })
          .catch(function () {
            if (ctaLabelEl) ctaLabelEl.textContent = originalLabel;
            addToCartBtn.classList.remove("is-loading");
            addToCartBtn.disabled = false;
            alert("Sorry, that item couldn't be added to your cart. Please try again.");
          });
      });
    }

    /* ---------- buy now ---------- */

    if (buyNowBtn) {
      buyNowBtn.addEventListener("click", function () {
        if (!currentVariant || buyNowBtn.disabled) return;

        var quantity = parseInt(root.dataset.currentQuantity, 10) || 1;
        var originalLabel = buyNowLabelEl ? buyNowLabelEl.textContent : "";
        var loadingText = buyNowBtn.dataset.loadingText || "redirecting…";

        buyNowBtn.classList.add("is-loading");
        buyNowBtn.disabled = true;
        if (addToCartBtn) addToCartBtn.disabled = true;
        if (buyNowLabelEl) buyNowLabelEl.textContent = loadingText;

        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            items: [{ id: currentVariant.id, quantity: quantity }],
          }),
        })
          .then(function (res) {
            if (!res.ok) throw new Error("Add to cart failed");
            return res.json();
          })
          .then(function () {
            window.location.href = "/checkout";
          })
          .catch(function () {
            if (buyNowLabelEl) buyNowLabelEl.textContent = originalLabel;
            buyNowBtn.classList.remove("is-loading");
            buyNowBtn.disabled = false;
            if (addToCartBtn) addToCartBtn.disabled = false;
            alert("Sorry, that item couldn't be added to your cart. Please try again.");
          });
      });
    }

    /* ---------- sticky mobile add to cart ---------- */

    if (stickyAddToCartBtn) {
      stickyAddToCartBtn.addEventListener("click", function () {
        if (!currentVariant || stickyAddToCartBtn.disabled) return;

        var quantity = parseInt(root.dataset.currentQuantity, 10) || 1;
        var originalLabel = stickyCtaLabelEl ? stickyCtaLabelEl.textContent : "";
        var addingText = stickyAddToCartBtn.dataset.addingText || "adding…";
        var addedText = stickyAddToCartBtn.dataset.addedText || "added ✓";

        stickyAddToCartBtn.classList.add("is-loading");
        stickyAddToCartBtn.disabled = true;
        if (stickyCtaLabelEl) stickyCtaLabelEl.textContent = addingText;

        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            items: [{ id: currentVariant.id, quantity: quantity }],
          }),
        })
          .then(function (res) {
            if (!res.ok) throw new Error("Add to cart failed");
            return res.json();
          })
          .then(function () {
            if (stickyCtaLabelEl) stickyCtaLabelEl.textContent = addedText;
            document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));
            if (window.OBVSLYCartDrawer && typeof window.OBVSLYCartDrawer.flyToCart === "function") {
              window.OBVSLYCartDrawer.flyToCart(stickyAddToCartBtn);
            }

            if (redirectToCart) {
              window.location.href = "/cart";
              return;
            }
            setTimeout(function () {
              stickyAddToCartBtn.classList.remove("is-loading");
              stickyAddToCartBtn.disabled = false;
              if (stickyCtaLabelEl) stickyCtaLabelEl.textContent = originalLabel;
            }, 1800);
          })
          .catch(function () {
            if (stickyCtaLabelEl) stickyCtaLabelEl.textContent = originalLabel;
            stickyAddToCartBtn.classList.remove("is-loading");
            stickyAddToCartBtn.disabled = false;
            alert("Sorry, that item couldn't be added to your cart. Please try again.");
          });
      });
    }

    paintSizePrices();
    updateStickyTitle();
    recalc();

    /* Stagger is set up last and is entirely optional — see the
       comment on initStagger() below. Everything above this line
       (cart, buttons, pricing) already works regardless of whether
       this succeeds. */
    initStagger(root);

    /* Sticky bar visibility on scroll — also entirely optional and
       additive; the bar simply stays hidden (its CSS default) if
       this doesn't run for any reason. */
    initStickyBar(root, stickyBarEl);
  }

  /*
     Scroll-in animation — pure CSS transitions, no library, no CDN
     request. Content is ALWAYS visible already (see the default
     [data-stagger-item] rule in product-showcase.css with no
     "hidden by default" state), so this function only ever ADDS a
     bit of motion on top when it can; if it doesn't run at all
     (JS disabled, an error elsewhere on the page, etc.) nothing is
     lost — content was already fully visible and functional.
  */
  function initStagger(root) {
    if (root.dataset.stagger === "false") return;
    if (typeof IntersectionObserver === "undefined") return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    var groupEls = Array.prototype.slice.call(root.querySelectorAll("[data-stagger-group]"));
    groupEls.forEach(function (groupEl, order) {
      var items = Array.prototype.slice.call(groupEl.querySelectorAll("[data-stagger-item]"));
      if (!items.length) return;

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            playGroup(items, order);
          });
        },
        { threshold: 0 }
      );
      observer.observe(groupEl);
    });
  }

  /*
     Sticky mobile add-to-cart bar — shows itself once the shopper
     scrolls down past the vertical middle of whichever section comes
     right after this one (detected generically via nextElementSibling
     of this section's own wrapper, so it works no matter which
     section a merchant places next in the theme editor), and hides
     itself again if they scroll back up above that point. Hidden on
     desktop entirely via CSS. If IntersectionObserver isn't available,
     the bar just stays in its default hidden CSS state — nothing else
     on the page depends on this running.

     FIX (bar was covering the footer, forcing an extra scroll just to
     see the site credit line at the very bottom): the bar is now also
     hidden the moment the footer comes into view at all, regardless of
     how far past the trigger section the shopper has scrolled, and
     reappears again if they scroll back up above the footer (as long
     as they're still past the original trigger point). The footer is
     found generically — first any element the theme marks with
     data-site-footer (the Footer Signup section does), falling back to
     the page's <footer> landmark — so this doesn't depend on which
     footer section happens to be installed.
  */
  function initStickyBar(root, stickyBarEl) {
    if (!stickyBarEl) return;
    if (root.dataset.stickyBarEnabled === "false") return;
    if (typeof IntersectionObserver === "undefined") return;

    var sectionWrapper = root.closest('[id^="shopify-section-"]');
    var targetSection = sectionWrapper ? sectionWrapper.nextElementSibling : null;
    if (!targetSection) return;

    var pastTriggerPoint = false;
    var footerInView = false;

    function updateVisibility() {
      if (pastTriggerPoint && !footerInView) {
        stickyBarEl.setAttribute("data-visible", "true");
      } else {
        stickyBarEl.removeAttribute("data-visible");
      }
    }

    var triggerObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            /* The middle of the viewport is currently inside the next
               section — i.e. the shopper has scrolled at least to its
               vertical center. */
            pastTriggerPoint = true;
          } else if (entry.boundingClientRect.top > 0) {
            /* Next section's middle hasn't been reached yet (it's
               still below the viewport center) — stay hidden. */
            pastTriggerPoint = false;
          }
          /* else: boundingClientRect.top <= 0 and not intersecting
             means the shopper has scrolled on past the trigger
             section entirely (further down the page) — pastTriggerPoint
             deliberately stays true here; it only flips back to false
             when they scroll back up above the trigger point, which is
             caught by the branch above. */
          updateVisibility();
        });
      },
      { rootMargin: "-50% 0px -50% 0px", threshold: 0 }
    );
    triggerObserver.observe(targetSection);

    var footerEl = document.querySelector("[data-site-footer], footer");
    if (footerEl) {
      var footerObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            footerInView = entry.isIntersecting;
            updateVisibility();
          });
        },
        { threshold: 0 }
      );
      footerObserver.observe(footerEl);
    }
  }

  function playGroup(items, order) {
    var groupDelay = order * GROUP_DELAY;

    /* Apply the "before" state to every item first... */
    items.forEach(function (item, i) {
      item.classList.add("stagger-pre");
      item.style.transitionDelay = (groupDelay + i * CONTENT_STAGGER) + "s";
    });

    /* ...force the browser to render that frame before switching to
       the "in" state, otherwise it would just snap straight to the
       end result with no visible transition at all. Two nested
       requestAnimationFrame calls reliably wait for that next paint
       across browsers. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        items.forEach(function (item) {
          item.classList.remove("stagger-pre");
          item.classList.add("stagger-in");
        });
      });
    });
  }

  document.querySelectorAll("[data-product-showcase]").forEach(init);

  /* ---------- ingredients modal (shared across all instances) ---------- */

  document.addEventListener("click", function (e) {
    if (e.target.closest("[data-open-ingredients]")) {
      var modal = document.querySelector("[data-ingredients-modal]");
      if (!modal) return;
      modal.hidden = false;
      document.body.style.overflow = "hidden";
      var closeBtn = modal.querySelector(".p-showcase__modal-close");
      if (closeBtn) closeBtn.focus();
    }
    if (e.target.closest("[data-close-ingredients]")) {
      var openModal = document.querySelector("[data-ingredients-modal]");
      if (openModal) {
        openModal.hidden = true;
        document.body.style.overflow = "";
      }
    }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") {
      var modal = document.querySelector("[data-ingredients-modal]:not([hidden])");
      if (modal) {
        modal.hidden = true;
        document.body.style.overflow = "";
      }
    }
  });
})();